import React, { useState } from "react";
import {
  Row,
  Col,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Tab,
  Nav,
  Card,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faMoneyBill,
  faInfoCircle,
  faExternalLinkAlt,
  faUser,
  faCalendarAlt,
  faFileInvoice,
  faTag,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import {
  VendaType,
  formatarValor,
  formatarDataVenda,
  formatarStatusVenda,
  getStatusVendaClass,
  ItemVendaType,
  PagamentoVendaType,
} from "data/venda";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import "./VendaExpandedRow.css";
import CustomBadge from "components/base/Badge";

interface VendaExpandedRowProps {
  venda: VendaType;
}

const VendaExpandedRow: React.FC<VendaExpandedRowProps> = ({ venda }) => {
  const [activeTab, setActiveTab] = useState("info");

  // Consulta para obter os detalhes da venda
  const { data: detalhes, isLoading } = useQuery({
    queryKey: ["vendaDetalhes", venda.id],
    queryFn: async () => {
      const { data } = await axios.get(`/api/venda/${venda.id}`);
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="p-3 text-center">
        <div
          className="spinner-border spinner-border-sm text-primary me-2"
          role="status"
        >
          <span className="visually-hidden">Carregando...</span>
        </div>
        Carregando detalhes...
      </div>
    );
  }

  if (!detalhes) {
    return (
      <div className="p-3 text-center text-danger">
        Não foi possível carregar os detalhes desta venda.
      </div>
    );
  }

  // Componente para informações gerais
  const InformacoesGerais = () => (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-3">
        <Row className="mb-3">
          <Col md={6}>
            <div className="d-flex align-items-center mb-3">
              <div className="icon-circle bg-primary-subtle me-2">
                <FontAwesomeIcon icon={faUser} className="text-primary" />
              </div>
              <div>
                <div className="text-body-tertiary fs-9">Cliente</div>
                <div className="fw-semibold">
                  {detalhes.cliente ? (
                    detalhes.clienteId ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Abrir cadastro do cliente em nova aba
                          </Tooltip>
                        }
                      >
                        <a
                          href={`/revenda/clientes/${detalhes.clienteId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none text-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {detalhes.cliente}
                          <FontAwesomeIcon
                            icon={faExternalLinkAlt}
                            className="ms-1 fa-xs text-muted"
                          />
                        </a>
                      </OverlayTrigger>
                    ) : (
                      detalhes.cliente
                    )
                  ) : (
                    "Cliente não identificado"
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center mb-3">
              <div className="icon-circle bg-success-subtle me-2">
                <FontAwesomeIcon icon={faUser} className="text-success" />
              </div>
              <div>
                <div className="text-body-tertiary fs-9">Vendedor</div>
                <div className="fw-semibold">{detalhes.usuario}</div>
              </div>
            </div>

            <div className="d-flex align-items-center mb-3">
              <div className="icon-circle bg-info-subtle me-2">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-info" />
              </div>
              <div>
                <div className="text-body-tertiary fs-9">Data</div>
                <div className="fw-semibold">
                  {formatarDataVenda(detalhes.data)}
                </div>
              </div>
            </div>

            {venda.empresaNomeFantasia && (
              <div className="d-flex align-items-center mb-3">
                <div className="icon-circle bg-primary-subtle me-2">
                  <FontAwesomeIcon icon={faBuilding} className="text-primary" />
                </div>
                <div>
                  <div className="text-body-tertiary fs-9">Empresa</div>
                  <div>
                    <CustomBadge variant="tag" className="mt-1">
                      {venda.empresaNomeFantasia}
                    </CustomBadge>
                  </div>
                </div>
              </div>
            )}
          </Col>

          <Col md={6}>
            <div className="d-flex align-items-center mb-3">
              <div className="icon-circle bg-warning-subtle me-2">
                <FontAwesomeIcon icon={faTag} className="text-warning" />
              </div>
              <div>
                <div className="text-body-tertiary fs-9">Status</div>
                <div>
                  <Badge
                    bg={getStatusVendaClass(detalhes.cancelado)}
                    className="px-2 py-1 fs-9"
                  >
                    {formatarStatusVenda(detalhes.cancelado)}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center mb-3">
              <div className="icon-circle bg-secondary-subtle me-2">
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="text-secondary"
                />
              </div>
              <div>
                <div className="text-body-tertiary fs-9">Nota Fiscal</div>
                <div className="fw-semibold">
                  {detalhes.numeroNFe > 0 ? detalhes.numeroNFe : "Não emitida"}
                </div>
              </div>
            </div>

            {detalhes.chaveNFe && (
              <div className="mb-3">
                <div className="text-body-tertiary fs-9">Chave NFe</div>
                <div
                  className="text-muted small text-truncate"
                  style={{ maxWidth: "300px" }}
                >
                  {detalhes.chaveNFe}
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Outras informações */}
        {detalhes.observacao && (
          <div className="mb-3 p-2 bg-body-tertiary rounded">
            <div className="text-body-tertiary fs-9">Observação</div>
            <div>{detalhes.observacao}</div>
          </div>
        )}

        {detalhes.cancelado && detalhes.motivoCancelamento && (
          <div className="p-2 bg-danger-subtle rounded border border-danger-subtle">
            <div className="text-danger fw-semibold fs-9">
              Motivo do Cancelamento
            </div>
            <div>{detalhes.motivoCancelamento}</div>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  // Componente para itens
  const Itens = () => (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-3">
        <div className="table-responsive">
          <Table hover className="mb-0 border-0">
            <thead className="bg-body-tertiary">
              <tr>
                <th>Produto</th>
                <th className="text-center">Qtd</th>
                <th className="text-end">Valor Unit.</th>
                <th className="text-end">Desconto</th>
                <th className="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              {detalhes.itens.map((item: ItemVendaType, index: number) => (
                <tr key={index}>
                  <td>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>Abrir cadastro do produto em nova aba</Tooltip>
                      }
                    >
                      <a
                        href={`/cadastros/produtos/${item.produtoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.descricao}
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          className="ms-1 fa-xs text-muted"
                        />
                      </a>
                    </OverlayTrigger>
                  </td>
                  <td className="text-center">{item.quantidade}</td>
                  <td className="text-end">
                    {formatarValor(item.valorUnitario)}
                  </td>
                  <td className="text-end">{formatarValor(item.desconto)}</td>
                  <td className="text-end fw-bold">
                    {formatarValor(item.valorTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );

  // Componente para pagamentos
  const Pagamentos = () => (
    <Card className="border-0 shadow-sm">
      <Card.Body className="p-3">
        <div className="table-responsive mb-3">
          <Table hover className="mb-0 border-0">
            <thead className="bg-body-tertiary">
              <tr>
                <th>Forma de Pagamento</th>
                <th className="text-end">Valor</th>
              </tr>
            </thead>
            <tbody>
              {detalhes.pagamentos.map(
                (pagamento: PagamentoVendaType, index: number) => (
                  <tr key={index}>
                    <td>{pagamento.descricao}</td>
                    <td className="text-end fw-bold">
                      {formatarValor(pagamento.valor)}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        </div>

        <Card className="bg-body-tertiary border-0">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between mb-2">
              <span className="text-body-tertiary">Valor Bruto:</span>
              <span>{formatarValor(detalhes.valorBruto)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-body-tertiary">Descontos:</span>
              <span className="text-danger">
                -{formatarValor(detalhes.descontoTotal)}
              </span>
            </div>
            <hr className="my-2" />
            <div className="d-flex justify-content-between fw-bold">
              <span>Valor Total:</span>
              <span>{formatarValor(detalhes.valorTotal)}</span>
            </div>
          </Card.Body>
        </Card>
      </Card.Body>
    </Card>
  );

  return (
    <div className="p-3">
      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "info")}
      >
        <Nav variant="pills" className="mb-3 nav-phoenix-pills d-flex">
          <Nav.Item className="flex-fill">
            <Nav.Link eventKey="info" className="px-3 text-center">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Informações
            </Nav.Link>
          </Nav.Item>
          <Nav.Item className="flex-fill">
            <Nav.Link eventKey="itens" className="px-3 text-center">
              <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
              Itens
            </Nav.Link>
          </Nav.Item>
          <Nav.Item className="flex-fill">
            <Nav.Link eventKey="pagamentos" className="px-3 text-center">
              <FontAwesomeIcon icon={faMoneyBill} className="me-2" />
              Pagamentos
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="info">
            <InformacoesGerais />
          </Tab.Pane>
          <Tab.Pane eventKey="itens">
            <Itens />
          </Tab.Pane>
          <Tab.Pane eventKey="pagamentos">
            <Pagamentos />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default VendaExpandedRow;
